/**
 * Apollo Client Configuration - Phase 3 Communication System
 * Sets up Apollo Client for GraphQL operations
 * Note: For subscriptions, use AWS Amplify API directly (see graphql service)
 */

import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { Auth } from 'aws-amplify';
import { useAuthStore } from '../store/authStore';

// GraphQL endpoint
const GRAPHQL_ENDPOINT = process.env.REACT_APP_GRAPHQL_ENDPOINT || 'https://your-graphql-endpoint.amazonaws.com/graphql';

console.log('🌐 GraphQL Endpoint:', GRAPHQL_ENDPOINT);

// Create HTTP Link for queries and mutations
const httpLink = createHttpLink({
  uri: GRAPHQL_ENDPOINT,
});

// Operations that should use API key (unauthenticated / auth mutations)
const API_KEY_OPERATIONS = ['signUp', 'signIn', 'confirmEmail', 'naverOAuth', 'kakaoOAuth', 'facebookOAuth', 'completeOAuthRegistration', 'checkUserExists', 'sendEmailVerification', 'confirmEmailVerification'];

// Auth Link for HTTP requests (queries and mutations)
const authLink = setContext(async (operation, { headers }: { headers?: Record<string, any> }) => {
  try {
    // Check if this operation should use API key (auth mutations)
    const operationName = operation.operationName || '';
    const isApiKeyOperation = API_KEY_OPERATIONS.some(op =>
      operationName.toLowerCase().includes(op.toLowerCase())
    );

    if (isApiKeyOperation) {
      const apiKey = process.env.REACT_APP_GRAPHQL_API_KEY;
      if (!apiKey) {
        console.error(
          '❌ REACT_APP_GRAPHQL_API_KEY is not set — API-key operation will fail:',
          operationName,
        );
      }
      return {
        headers: {
          ...headers,
          ...(apiKey ? { 'x-api-key': apiKey } : {}),
        }
      };
    }

    // Always try Auth.currentSession() first — it auto-refreshes expired tokens
    let token: string | null = null;
    try {
      const session = await Auth.currentSession();
      token = session.getIdToken().getJwtToken();
      // Update the store with the fresh token
      useAuthStore.setState({ token });
    } catch {
      // No Amplify session — fall back to stored token (e.g. Naver OAuth)
      token = useAuthStore.getState().token;
    }

    if (token) {
      return {
        headers: {
          ...headers,
          authorization: `Bearer ${token}`,
        }
      };
    }

    // No token available
    console.warn('⚠️ No auth token available for operation:', operationName);
    return { headers };
  } catch (error) {
    console.error('❌ HTTP Auth error:', error);
    return { headers };
  }
});

// 🚀 Create Apollo Client with optimized caching
// Note: Apollo Client subscriptions with AppSync require complex setup
// Use AWS Amplify API.graphql() for subscriptions instead (better compatibility)
const apolloClient = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          // Cache public campaigns for 5 minutes
          getPublicCampaigns: {
            keyArgs: ['filters'],
            merge(existing, incoming) {
              return incoming;
            },
          },
          // Cache public influencers for 5 minutes
          getPublicInfluencers: {
            keyArgs: ['filters'],
            merge(existing, incoming) {
              return incoming;
            },
          },
          // Cache individual public campaign for 10 minutes
          getPublicCampaign: {
            keyArgs: ['campaignId'],
            merge(existing, incoming) {
              return incoming;
            },
          },
          // Cache individual public influencer for 10 minutes
          getPublicInfluencer: {
            keyArgs: ['influencerId'],
            merge(existing, incoming) {
              return incoming;
            },
          },
        },
      },
      PublicCampaign: {
        keyFields: ['campaignId'],
      },
      PublicInfluencer: {
        keyFields: ['influencerId'],
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'ignore',
      fetchPolicy: 'cache-first', // Use cache first for better performance
      nextFetchPolicy: 'cache-first',
    },
    query: {
      errorPolicy: 'all',
      fetchPolicy: 'cache-first', // Use cache first for public data
    },
  },
  devtools: {
    enabled: process.env.NODE_ENV === 'development',
  },
});

console.log('🎯 Apollo Client initialized:', {
  endpoint: GRAPHQL_ENDPOINT,
  note: 'Use Amplify API for subscriptions'
});

export default apolloClient;
